import type { ApplicabilityMatch, ChecklistItem, CompanyProfile, RegulatoryRecord } from '../../data/regulations/types'
import { APPLICABILITY_LABELS, isActionable } from './applicability'
import { formatDeadlineDate } from './dates'

const OWNERS: Record<string, string> = {
  climate: 'Sustainability and operations',
  ghg: 'Sustainability and operations',
  carbon_registration: 'Sustainability, with finance if credits will be traded',
  esg_disclosure: 'Company secretary, investor relations and sustainability',
  sustainable_finance: 'Risk and finance',
  climate_financial_risk: 'Risk and finance',
  waste: 'EHS and operations',
  environment: 'EHS and operations',
  energy: 'Energy and operations',
  water: 'EHS and operations',
  biodiversity: 'EHS',
  labour: 'Human resources and HSE',
  product_supply_chain: 'Procurement and sustainability',
  cross_border: 'Trade compliance and sustainability',
  other: 'Sustainability',
}

export function buildChecklist(records: RegulatoryRecord[], matches: ApplicabilityMatch[]): ChecklistItem[] {
  const byId = new Map(records.map((record) => [record.id, record]))
  const items: ChecklistItem[] = []
  for (const match of matches) {
    if (!isActionable(match.state)) continue
    const record = byId.get(match.recordId)
    if (!record) continue
    const confirmed = record.deadlines.find((deadline) => deadline.certainty === 'confirmed' && deadline.date && (deadline.type === 'filing' || deadline.type === 'compliance'))
    const source = record.officialSources[0]
    items.push({
      id: `check:${record.id}`,
      recordId: record.id,
      slug: record.slug,
      title: record.shortTitle,
      why: match.reasons[0] || record.applicabilitySummary,
      action: record.recommendedActions[0] || 'Review the official source and confirm whether any condition applies to this entity.',
      owner: OWNERS[record.categories[0]] || 'Sustainability',
      sourceUrl: source?.url || '',
      sourceTitle: source?.title || 'Official source',
      deadlineLabel: confirmed ? `${formatDeadlineDate(confirmed.date)} — ${confirmed.label}` : 'No confirmed deadline identified',
      deadlineDate: confirmed?.date,
      deadlineCertainty: confirmed?.certainty,
      applicability: match.state,
      verificationNeeded: match.unverified,
    })
  }
  const rank = {
    further_verification_required: 0,
    likely_relevant: 1,
    potentially_relevant: 2,
    voluntary_or_market_driven: 3,
    no_trigger_identified: 4,
  }
  items.sort((a, b) => rank[a.applicability] - rank[b.applicability] || a.title.localeCompare(b.title))
  return items
}

export type ChecklistExport = {
  title: string
  generatedLabel: string
  disclaimer: string
  profileNote: string
  items: {
    title: string
    applicability: string
    why: string
    action: string
    owner: string
    deadline: string
    source: string
    verification: string
  }[]
}

export function buildChecklistExport(
  items: ChecklistItem[],
  options: { hiddenIds?: string[]; completedIds?: string[]; generatedOn?: string } = {},
): ChecklistExport {
  const hidden = new Set(options.hiddenIds || [])
  const done = new Set(options.completedIds || [])
  const visible = items.filter((item) => !hidden.has(item.id))
  return {
    title: 'UAE regulatory action checklist — preliminary planning aid',
    generatedLabel: options.generatedOn ? `Generated ${options.generatedOn}` : 'Generated in the browser',
    disclaimer:
      'This checklist is a preliminary planning aid. Marking an item complete does not prove legal compliance. It is not legal advice or a determination that a rule applies.',
    profileNote: 'Company profile answers used to build this list stayed in the browser and are not included in this export.',
    items: visible.map((item) => ({
      title: `${done.has(item.id) ? '[Done] ' : ''}${item.title}`,
      applicability: APPLICABILITY_LABELS[item.applicability],
      why: item.why,
      action: item.action,
      owner: item.owner,
      deadline: item.deadlineLabel,
      source: item.sourceUrl,
      verification: item.verificationNeeded.length ? item.verificationNeeded.join('; ') : 'None recorded beyond the official source.',
    })),
  }
}

export function checklistToPlainText(exported: ChecklistExport): string {
  const lines = [exported.title, exported.generatedLabel, '', exported.disclaimer, exported.profileNote, '']
  exported.items.forEach((item, index) => {
    lines.push(
      `${index + 1}. ${item.title}`,
      `Status: ${item.applicability}`,
      `Why it may be relevant: ${item.why}`,
      `Suggested action: ${item.action}`,
      `Suggested owner: ${item.owner}`,
      `Deadline: ${item.deadline}`,
      `Source: ${item.source}`,
      `Verification needed: ${item.verification}`,
      '',
    )
  })
  if (!exported.items.length) lines.push('No checklist items were selected.')
  return lines.join('\n')
}

export type ProductCta = { id: string; title: string; body: string; href: string }

export function productCtas(profile: CompanyProfile, matches: ApplicabilityMatch[]): ProductCta[] {
  const ctas: ProductCta[] = []
  if (profile.ghgInventory !== 'yes') {
    ctas.push({
      id: 'carbon',
      title: 'Carbon accounting demonstration',
      body: 'If emissions data is still scattered across sites and spreadsheets, Ecops can show a structured inventory workflow. That demonstration does not decide whether a legal reporting duty applies.',
      href: '/contact',
    })
  }
  if (profile.multiFacility === 'yes') {
    ctas.push({
      id: 'facilities',
      title: 'Multi-facility emissions tracking',
      body: 'Several facilities usually mean several data owners. Ecops centralises activity data so each site can be reviewed in one inventory. Legal applicability still has to be confirmed outside the software.',
      href: '/platform',
    })
  }
  if (profile.officialNotice === 'yes' || matches.some((match) => match.state === 'further_verification_required')) {
    ctas.push({
      id: 'evidence',
      title: 'Evidence and review workflow',
      body: 'Where a notice or an open verification point exists, keep the source document, the calculation and the approval together. Ecops supports record-keeping and review. It is not a legal opinion.',
      href: '/platform',
    })
  }
  return ctas
}
